/** Plugin catalogue page — list/card view with search + category filter (DD-09). */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid2 as Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { usePlugins } from "@/hooks/usePlugins";

const CATEGORIES = ["All", "Accelo", "Zendesk", "Datadog", "Reporting", "Analysis"] as const;

const CATEGORY_COLORS: Record<string, string> = {
  accelo: "#9678D3",
  zendesk: "#03363D",
  datadog: "#632CA6",
  reporting: "#041E42",
  analysis: "#71CC9B",
};

export default function Plugins() {
  const { data, isLoading } = usePlugins();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("All");
  const [view, setView] = useState<"list" | "card">("list");
  const [sortKey, setSortKey] = useState<"name" | "category" | "secure">("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: "name" | "category" | "secure") => {
    setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const filtered = useMemo(() => {
    const plugins = data?.items ?? [];
    const matched = plugins.filter((p) => {
      const matchesSearch =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.description.toLowerCase().includes(search.toLowerCase());
      const matchesCategory =
        category === "All" || p.category.toLowerCase() === category.toLowerCase();
      return matchesSearch && matchesCategory;
    });
    matched.sort((a, b) => {
      const av = sortKey === "secure" ? String(a.secure) : String(a[sortKey] ?? "");
      const bv = sortKey === "secure" ? String(b.secure) : String(b[sortKey] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return matched;
  }, [data, search, category, sortKey, sortDir]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Flow Catalogue</Typography>
        <Box>
          <Tooltip title="List view">
            <IconButton onClick={() => setView("list")} color={view === "list" ? "primary" : "default"}>
              <ViewListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Card view">
            <IconButton onClick={() => setView("card")} color={view === "card" ? "primary" : "default"}>
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search flows…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 250 }}
        />
        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(_, v) => v && setCategory(v)}
          size="small"
        >
          {CATEGORIES.map((c) => (
            <ToggleButton key={c} value={c}>
              {c}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {view === "list" ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><TableSortLabel active={sortKey === "name"} direction={sortKey === "name" ? sortDir : "asc"} onClick={() => handleSort("name")}>Name</TableSortLabel></TableCell>
                <TableCell><TableSortLabel active={sortKey === "category"} direction={sortKey === "category" ? sortDir : "asc"} onClick={() => handleSort("category")}>Category</TableSortLabel></TableCell>
                <TableCell>Description</TableCell>
                <TableCell><TableSortLabel active={sortKey === "secure"} direction={sortKey === "secure" ? sortDir : "asc"} onClick={() => handleSort("secure")}>Secure</TableSortLabel></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((plugin) => (
                <TableRow key={plugin.pluginId} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/plugins/${plugin.pluginId}`)}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{plugin.name}</Typography></TableCell>
                  <TableCell>
                    <Chip label={plugin.category} size="small" sx={{ bgcolor: CATEGORY_COLORS[plugin.category.toLowerCase()] ?? "#666", color: "#fff" }} />
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{plugin.description}</Typography></TableCell>
                  <TableCell>{plugin.secure ? <Chip icon={<LockIcon />} label="Secure" size="small" color="warning" /> : null}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No flows found.</Typography>
          )}
        </TableContainer>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((plugin) => (
            <Grid key={plugin.pluginId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card>
                <CardActionArea onClick={() => navigate(`/plugins/${plugin.pluginId}`)}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography variant="h6" noWrap>{plugin.name}</Typography>
                      {plugin.secure && <Chip icon={<LockIcon />} label="Secure" size="small" color="warning" />}
                    </Box>
                    <Chip label={plugin.category} size="small" sx={{ bgcolor: CATEGORY_COLORS[plugin.category.toLowerCase()] ?? "#666", color: "#fff", mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>{plugin.description}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
          {filtered.length === 0 && (
            <Grid size={12}>
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>No flows found.</Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
